import {
  CompRef,
  PresetsValue,
  WidgetDescriptor,
} from '@wix/document-services-types';
import { ComponentDefinition } from '@wix/editor-platform-sdk-types';
import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import { getWidgetAppearanceData } from './addBlocksWidget';

const TOKEN = 'unified-components-installation';

async function loadManifest(
  widgetId: string,
  componentType: string,
  context: ExtendedPlatformContext,
) {
  try {
    await context.manifestServices.internal?.loadAndResolveManifestsByCompTypes(
      [componentType],
    );
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.manifestLoadingFailed,
      'Could not load manifest',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(widgetId, 'builder-component')
      .withParentError(error as Error);
  }
}

export async function addBuilderComponent(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDescriptor: WidgetDescriptor,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const { widgetId, componentModel } = widgetDefinition;
  const isStretched = !!widgetDefinition.isStretched?.desktop;
  const { presetId, layout, layouts } = getWidgetAppearanceData(
    widgetDefinition,
    widgetDescriptor,
    addToStageData.presetId,
    addToStageData.position,
    isStretched,
  );
  const componentType = componentModel.componentType;

  const componentDefinition: ComponentDefinition = {
    componentType,
    type: 'Component',
    layout,
    layouts,
  };

  if (presetId) {
    componentDefinition.presets = {
      type: 'PresetData',
      layout: presetId,
      style: presetId,
    } as PresetsValue;
  }

  try {
    await loadManifest(widgetId, componentType, context);

    const compRef = await context.platformApiMethods.document.components.add(
      appData,
      TOKEN,
      {
        componentDefinition,
        pageRef: containerRef,
      },
    );

    return compRef;
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBuilderComponentFailed,
      'Could not add builder component',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(widgetId, 'builder-component')
      .withParentError(error as Error);
  }
}
